/**
 * GLB Decryption Utility (Browser)
 * 
 * Client-side decryption for GLB files using the Web Crypto API.
 * Requires HTTPS or localhost (secure context).
 * 
 * For Node.js environments, use decrypt.js instead.
 */

/**
 * Convert hex string to Uint8Array
 * @param {string} hex - Hex string (e.g., "a1b2c3d4...")
 * @returns {Uint8Array} - Byte array
 */
function hexToBytes(hex) {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16)
  }
  return bytes
}

/**
 * Decrypt GLB file using Web Crypto API (browser)
 * @param {ArrayBuffer} encryptedData - The encrypted data with IV prepended
 * @param {string} keyHex - The encryption key as 64-character hex string
 * @returns {Promise<ArrayBuffer>} - Decrypted GLB data
 * @throws {Error} - If decryption fails or crypto API unavailable
 */
export async function decryptGLB(encryptedData, keyHex) {
  try {
    // Check if crypto.subtle is available (requires secure context: HTTPS or localhost)
    if (!window.crypto || !window.crypto.subtle) {
      console.warn('⚠️ crypto.subtle not available (insecure context)')
      console.warn('   Web Crypto API requires HTTPS or localhost')
      throw new Error('Crypto API not available - requires HTTPS or localhost')
    }

    // Validate inputs
    if (!encryptedData || encryptedData.byteLength < 16) {
      throw new Error('Invalid encrypted data: too short or empty')
    }

    if (!keyHex || keyHex.length !== 64) {
      throw new Error('Invalid encryption key: must be 64 hex characters')
    }

    // Convert encrypted data to Uint8Array
    const encryptedArray = new Uint8Array(encryptedData)

    // Extract IV (first 16 bytes) and encrypted content
    const iv = encryptedArray.slice(0, 16)
    const encrypted = encryptedArray.slice(16)

    // Import the encryption key
    const keyBytes = hexToBytes(keyHex)
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-CBC' },
      false,
      ['decrypt']
    )

    // Decrypt using AES-CBC
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      cryptoKey,
      encrypted
    )

    return decrypted
  } catch (error) {
    console.error('❌ GLB decryption failed:', error)
    throw new Error(`Failed to decrypt GLB: ${error.message}`)
  }
}

/**
 * Check if the current context supports crypto operations
 * @returns {boolean} - True if Web Crypto API is available
 */
export function isSecureContext() {
  return window.isSecureContext && !!window.crypto?.subtle
}

/**
 * Fetch and decrypt a GLB file from a URL
 * @param {string} url - URL to encrypted GLB file
 * @param {string} encryptionKey - Encryption key as hex string
 * @returns {Promise<ArrayBuffer>} - Decrypted GLB data
 */
export async function fetchAndDecrypt(url, encryptionKey) {
  const response = await fetch(url)
  
  if (!response.ok) {
    throw new Error(`Failed to fetch encrypted model: ${response.status} ${response.statusText}`)
  }

  const encryptedData = await response.arrayBuffer()
  return decryptGLB(encryptedData, encryptionKey)
}

/**
 * Create a blob URL from decrypted GLB data
 * Useful for loading with GLTFLoader
 * @param {ArrayBuffer} decryptedData - Decrypted GLB data
 * @returns {string} - Blob URL (remember to revoke with URL.revokeObjectURL)
 */
export function createBlobURL(decryptedData) {
  const blob = new Blob([decryptedData], { type: 'model/gltf-binary' })
  return URL.createObjectURL(blob)
}
